import { FeatureFlagEnum } from "@finway-group/shared/lib/models"
import { RightOutlined } from "@ant-design/icons"
import { ArrowLeft as ArrowLeftIcon } from "react-feather"
import { Button, Card, Col, List, Row } from "antd"
import React, { Suspense, useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { NavLink, Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from "react-router-dom"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { logout } from "Shared/store/actions/auth/authActions"
import { useFeatureFlags } from "Shared/hooks/featureFlags.hooks"
import { hasFeatureFlags } from "Shared/utils/featureFlags"
import SettingsDetails from "./settingsDetails"
import { insertIf } from "Shared/utils"
import { useCompany } from "Shared/hooks/company.hooks"

const CostCenter2Settings = React.lazy(() => import("./costCenter2Settings"))
const ExpenseAccountSettings = React.lazy(() => import("./expenseAccountSettings"))
const IntegrationSettings = React.lazy(() => import("./integration/integrationsContainer"))
const TaxSettings = React.lazy(() => import("./taxSettings"))
const TeamSettings = React.lazy(() => import("./teamSettings"))
const UserProfileSettings = React.lazy(() => import("./userProfileSettings"))
const ToolsSecurityContainer = React.lazy(() => import("./toolsAndSecurity/toolsAndSecurityContainer"))
const PaymentSettingsContainer = React.lazy(() => import("./payment/paymentSettingsContainer"))
const CompanySettings = React.lazy(() => import("./companySettings/companySettings"))
const CompanyList = React.lazy(() => import("./companySettings/companyList"))
const BankSettings = React.lazy(() => import("./bankSettings"))
const NotificationSettingsContainer = React.lazy(() => import("./notificationSettings/notificationSettingsContainer"))
const ExportHistoryList = React.lazy(() => import("./exportHistoryList"))

interface SettingsInterface {}

export interface SettingsRouteMatchInterface {
    settingsTab: string
}
export const paymentSettingsLink = "payment"

const SettingsContainer = ({}: SettingsInterface) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const location = useLocation()
    const company = useCompany()
    const featureFlags = useFeatureFlags()

    const history = useHistory()

    const [selectedMenuItem, setSelectedMenuItem] = useState(0)

    const currentEmployee = useLoggedInEmployeeProfile()

    useEffect(() => {
        if (!currentEmployee || !currentEmployee.id) {
            dispatch(logout())
        }
    }, [])

    useEffect(() => {
        let mounted = true

        if (mounted) {
            const itemIndex = filterMenuList().findIndex((menuItem) => location.pathname.includes(menuItem.link))
            setSelectedMenuItem(itemIndex)
        }

        return () => {
            mounted = false
        }
    }, [location])

    const menuList = [
        { link: "profile", title: t("label:profile"), requiredFeatureFlags: [], component: <UserProfileSettings /> },
        { link: "tools-security", title: t("label:tool_settings_security"), requiredFeatureFlags: [], component: <ToolsSecurityContainer /> },
        { link: "company-settings", title: t("label:company_settings"), requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION], adminOnly: true, component: <CompanySettings /> },
        ...insertIf(company.isRoot, {
            link: paymentSettingsLink,
            title: t("label:payment_settings.title"),
            requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION, FeatureFlagEnum.STRIPE_ENABLED],
            adminOnly: true,
            component: <PaymentSettingsContainer />,
        }),
        {
            link: "sub-companies",
            title: t("label:companies"),
            requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION, FeatureFlagEnum.SUB_COMPANIES_ENABLED],
            adminOnly: true,
            component: <CompanyList />,
        },
        { link: "bank-accounts", title: t("label:bank_accounts"), requiredFeatureFlags: [], adminOnly: true, component: <BankSettings /> },
        {
            link: "expense-accounts",
            title: t("label:expense_accounts"),
            requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION],
            adminOnly: true,
            component: <ExpenseAccountSettings />,
        },
        { link: "costcenter-2", title: t("label:cost_center_2"), requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION], adminOnly: true, component: <CostCenter2Settings /> },
        { link: "vat", title: t("label:vat_title"), requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION], adminOnly: true, component: <TaxSettings /> },
        { link: "teams", title: t("label:teams"), requiredFeatureFlags: [], adminOnly: true, component: <TeamSettings /> },
        { link: "integrations", title: t("label:integrations"), requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION], adminOnly: true, component: <IntegrationSettings /> },
        { link: "notifications", title: t("label:notifications.title"), requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION], component: <NotificationSettingsContainer /> },
        { link: "export-history", title: t("label:export_history.title"), requiredFeatureFlags: [FeatureFlagEnum.PAID_VERSION], component: <ExportHistoryList /> },
    ]

    const filterMenuList = () =>
        menuList.filter((menuItem) => hasFeatureFlags(featureFlags, menuItem.requiredFeatureFlags) && (currentEmployee.activeCompanyProfile.isAdmin || !menuItem.adminOnly))

    /**
     *
     * @param match
     * @param index
     * @returns
     */
    const handleMenuItemSelection = (match: any, index: number) => !!match

    const { path, url } = useRouteMatch()

    const isParent = location.pathname.replace(`${path}`, "").length === 0

    return (
        <div>
            <Helmet>
                <title>{t("view:settings.title")}</title>
            </Helmet>

            {history.location.state === "liquidity" && (
                <Button className="mb-16" onClick={() => history.goBack()}>
                    <ArrowLeftIcon />
                    {t("label:liquidity.return")}
                </Button>
            )}

            <h1 className="mb-32">{t("view:settings.title")}</h1>

            <Card className="settings-card h-screen-20 pb-100">
                <Row gutter={[16, 16]} className="m-0 p-0 justify-start sm:justify-center">
                    <Col xs={24} md={6} className={`p-0 ${!isParent && "hidden"} sm:block overflow-y-auto h-screen-25`}>
                        <List
                            style={{ borderColor: "#EEF4F8" }}
                            dataSource={filterMenuList()}
                            renderItem={(item, index) => (
                                <NavLink
                                    key={index}
                                    to={`${url}/${item.link}`}
                                    activeClassName="active"
                                    onClick={() => setSelectedMenuItem(index)}
                                    isActive={(match) => handleMenuItemSelection(match, index)}
                                >
                                    <List.Item
                                        key={index}
                                        style={{ border: "solid 1px #EEF4F8" }}
                                        className={`settings-menu-item ${selectedMenuItem === index ? "active" : ""}`}
                                        extra={<RightOutlined key="outlineIcon" />}
                                    >
                                        <span className="text-base leading-relaxed">{item.title}</span>
                                    </List.Item>
                                </NavLink>
                            )}
                            bordered
                        />
                    </Col>
                    <Col xs={24} md={18} className={`md:mt-10 ${isParent && "hidden"} sm:block`}>
                        <SettingsDetails>
                            <Switch>
                                <Route exact path={path}>
                                    <UserProfileSettings />
                                </Route>
                                {filterMenuList().map((menuItem, index) => (
                                    <Route key={index} path={`/settings/${menuItem.link}/:settingsTab?`}>
                                        <Suspense fallback={<></>}>{menuItem.component}</Suspense>
                                    </Route>
                                ))}

                                <Redirect key="redirect" from="*" to="/settings/profile" />
                            </Switch>
                        </SettingsDetails>
                    </Col>
                </Row>
            </Card>
        </div>
    )
}

export default React.memo(SettingsContainer)
